import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  FileText, 
  Download, 
  Search, 
  Calendar, 
  User, 
  Clock, 
  MapPin,
  Building2,
  ChevronDown,
  ChevronUp,
  Eye,
  X,
  Car
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EncounterDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [encounters, setEncounters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [viewingEncounter, setViewingEncounter] = useState(null);
  const [dateFilter, setDateFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');

  const fetchEncounters = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/encounters?skip=${page * 20}&limit=20`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setEncounters(data.encounters || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching encounters:', error);
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    fetchEncounters();
  }, [fetchEncounters]);

  const downloadPdf = async (encounterId) => {
    try {
      const res = await fetch(`${API}/api/encounters/${encounterId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `encounter_${encounterId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const viewEncounterDetails = async (encounterId) => {
    try {
      const res = await fetch(`${API}/api/encounters/${encounterId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setViewingEncounter(data);
      }
    } catch (error) {
      console.error('Error fetching encounter details:', error);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const filteredEncounters = encounters.filter(enc => {
    const matchesSearch = searchTerm === '' || 
      (enc.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (enc.client_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (enc.work_order_id?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (enc.representative_name?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesDate = dateFilter === '' || 
      (enc.submitted_at && enc.submitted_at.startsWith(dateFilter));
    
    const matchesVendor = vendorFilter === '' ||
      (enc.vendor_name?.toLowerCase().includes(vendorFilter.toLowerCase()));
    
    return matchesSearch && matchesDate && matchesVendor;
  });

  return (
    <div className="space-y-6" data-testid="encounter-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Encounter Forms</h1>
          <p className="text-slate-500 mt-1">Review submitted encounter forms from vendors</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-600 bg-blue-50 px-4 py-2 rounded-lg">
          <FileText className="w-4 h-4 text-blue-600" />
          <span className="font-medium">{total}</span> Total Submissions
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by vendor, client, work order, representative..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="encounter-search"
              />
            </div>
            <div className="flex gap-2">
              <Input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-40"
                data-testid="encounter-date-filter"
              />
              <Input
                placeholder="Filter by vendor"
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
                className="w-40"
                data-testid="encounter-vendor-filter"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Encounter List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredEncounters.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No Encounter Forms Found</h3>
            <p className="text-slate-500 mt-1">
              {searchTerm || dateFilter || vendorFilter 
                ? 'Try adjusting your filters'
                : 'Encounter forms will appear here when vendors submit them'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredEncounters.map((encounter) => (
            <Card 
              key={encounter.encounter_id} 
              className="overflow-hidden hover:shadow-md transition-shadow"
              data-testid={`encounter-card-${encounter.encounter_id}`}
            >
              <div 
                className="p-4 cursor-pointer"
                onClick={() => setExpandedId(expandedId === encounter.encounter_id ? null : encounter.encounter_id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Work Order & Vendor */}
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Work Order</p>
                      <p 
                        className="font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/work-orders/${encounter.work_order_id}`);
                        }}
                      >
                        {encounter.work_order_id}
                      </p>
                      <div 
                        className="flex items-center gap-1 mt-1 text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (encounter.vendor_id) navigate(`/vendors/${encounter.vendor_id}`);
                        }}
                      >
                        <User className="w-3 h-3" />
                        {encounter.vendor_name}
                        {encounter.vendor_number && (
                          <span className="text-slate-400">({encounter.vendor_number})</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Client */}
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Client</p>
                      <div className="flex items-center gap-1 text-sm">
                        <Building2 className="w-3 h-3 text-slate-400" />
                        <span className="font-medium">{encounter.client_name || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-1 text-sm text-slate-600">
                        <span>Rep: {encounter.representative_name}</span>
                      </div>
                    </div>
                    
                    {/* Time */}
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Service Time</p>
                      <div className="flex items-center gap-1 text-sm">
                        <Clock className="w-3 h-3 text-slate-400" />
                        <span>{encounter.actual_start_time} - {encounter.actual_end_time}</span>
                      </div>
                      {encounter.actual_travel_time_minutes && (
                        <div className="flex items-center gap-1 mt-1 text-sm text-slate-600">
                          <Car className="w-3 h-3 text-slate-400" />
                          <span>{encounter.actual_travel_time_minutes} min travel</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Submitted */}
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Submitted</p>
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="w-3 h-3 text-slate-400" />
                        <span>{formatDateTime(encounter.submitted_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        viewEncounterDetails(encounter.encounter_id);
                      }}
                      data-testid={`view-encounter-${encounter.encounter_id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadPdf(encounter.encounter_id);
                      }}
                      data-testid={`download-encounter-${encounter.encounter_id}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    {expandedId === encounter.encounter_id ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>
              </div>
              
              {/* Expanded Details */}
              {expandedId === encounter.encounter_id && (
                <div className="px-4 pb-4 pt-0 border-t bg-slate-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4">
                    {/* Travel Details */}
                    <div className="bg-white p-3 rounded-lg border">
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Travel Details</p>
                      <div className="space-y-1 text-sm">
                        {encounter.estimated_travel_distance_miles && (
                          <p><span className="text-slate-500">Est. Distance:</span> {encounter.estimated_travel_distance_miles} mi</p>
                        )}
                        {encounter.estimated_travel_time_minutes && (
                          <p><span className="text-slate-500">Est. Time:</span> {encounter.estimated_travel_time_minutes} min</p>
                        )}
                        {encounter.actual_travel_distance_miles && (
                          <p><span className="text-slate-500">Actual Distance:</span> {encounter.actual_travel_distance_miles} mi</p>
                        )}
                        {encounter.actual_travel_time_minutes && (
                          <p><span className="text-slate-500">Actual Time:</span> {encounter.actual_travel_time_minutes} min</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Representative */}
                    <div className="bg-white p-3 rounded-lg border">
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Client Representative</p>
                      <div className="space-y-1 text-sm">
                        <p className="font-medium">{encounter.representative_name}</p>
                        {encounter.representative_title && (
                          <p className="text-slate-600">{encounter.representative_title}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Services */}
                    <div className="bg-white p-3 rounded-lg border">
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Services Rendered</p>
                      <p className="text-sm">{encounter.services_rendered || 'Not specified'}</p>
                    </div>
                    
                    {/* Notes */}
                    <div className="bg-white p-3 rounded-lg border">
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Notes</p>
                      <p className="text-sm">{encounter.notes || 'No notes'}</p>
                    </div>
                  </div>
                  
                  {/* Signature Preview */}
                  {encounter.signature_data && (
                    <div className="mt-4 bg-white p-3 rounded-lg border">
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Client Signature</p>
                      <div className="bg-slate-100 p-2 rounded inline-block">
                        <img 
                          src={encounter.signature_data} 
                          alt="Client Signature" 
                          className="max-h-16 max-w-xs"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">
            Showing {page * 20 + 1} - {Math.min((page + 1) * 20, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * 20 >= total}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Encounter Detail Modal */}
      {viewingEncounter && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Encounter Form Details</h2>
                <p className="text-sm text-slate-500">{viewingEncounter.encounter_id}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadPdf(viewingEncounter.encounter_id)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewingEncounter(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Assignment Info */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Assignment Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Work Order</p>
                    <p className="font-medium text-blue-600">{viewingEncounter.work_order_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Assignment ID</p>
                    <p className="font-medium">{viewingEncounter.assignment_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Vendor</p>
                    <p className="font-medium">{viewingEncounter.vendor_name}</p>
                    <p className="text-sm text-slate-500">{viewingEncounter.vendor_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Client</p>
                    <p className="font-medium">{viewingEncounter.client_name}</p>
                  </div>
                </div>
              </div>
              
              {/* Service Times */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Service Times</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Actual Start Time</p>
                    <p className="font-medium">{viewingEncounter.actual_start_time}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Actual End Time</p>
                    <p className="font-medium">{viewingEncounter.actual_end_time}</p>
                  </div>
                </div>
              </div>
              
              {/* Travel Information */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Travel Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-xs text-blue-600 font-medium">Estimated</p>
                    <p className="text-sm mt-1">
                      {viewingEncounter.estimated_travel_distance_miles 
                        ? `${viewingEncounter.estimated_travel_distance_miles} miles` 
                        : 'N/A'}
                    </p>
                    <p className="text-sm">
                      {viewingEncounter.estimated_travel_time_minutes 
                        ? `${viewingEncounter.estimated_travel_time_minutes} minutes` 
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-xs text-green-600 font-medium">Actual</p>
                    <p className="text-sm mt-1">
                      {viewingEncounter.actual_travel_distance_miles 
                        ? `${viewingEncounter.actual_travel_distance_miles} miles` 
                        : 'N/A'}
                    </p>
                    <p className="text-sm">
                      {viewingEncounter.actual_travel_time_minutes 
                        ? `${viewingEncounter.actual_travel_time_minutes} minutes` 
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Services & Notes */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Services & Notes</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-slate-500">Services Rendered</p>
                    <p className="mt-1">{viewingEncounter.services_rendered || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Notes</p>
                    <p className="mt-1">{viewingEncounter.notes || 'No notes'}</p>
                  </div>
                </div>
              </div>
              
              {/* Client Representative & Signature */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Client Representative Signature</h3>
                <div className="flex items-start gap-6">
                  <div>
                    <p className="text-xs text-slate-500">Name</p>
                    <p className="font-medium">{viewingEncounter.representative_name}</p>
                    {viewingEncounter.representative_title && (
                      <p className="text-sm text-slate-500">{viewingEncounter.representative_title}</p>
                    )}
                  </div>
                  {viewingEncounter.signature_data && (
                    <div className="flex-1">
                      <p className="text-xs text-slate-500 mb-2">Signature</p>
                      <div className="bg-slate-100 p-3 rounded-lg border-b-2 border-slate-300 inline-block">
                        <img 
                          src={viewingEncounter.signature_data} 
                          alt="Client Signature" 
                          className="max-h-24"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Submission Info */}
              <div className="pt-4 border-t">
                <p className="text-xs text-slate-500">
                  Submitted on {formatDateTime(viewingEncounter.submitted_at)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
